import {PresenzeGiornataPlusRecord} from "@commons/mapping/PresenzeGiornataPlusRecord";
import {FlexRecord, ScartiFlex} from "@commons/mapping/FlexRecord";
import {CoppiaTimbrature} from "@commons/mapping/CoppiaTimbrature";
import {Calc, Coppia} from "@commons/mapping/Calc";
import {ObjectFactory} from "@commons/types";
import {merge} from "lodash";

export interface ScartiFlexCalcolo extends ScartiFlex {
    positivaIntera: number,
    overflow: boolean,
    index: number,
    versoEntrata: boolean,
}

export class ScartiFlexCalcolo {
    static factory: ObjectFactory<ScartiFlexCalcolo> = (json?) => {
        return merge<ScartiFlexCalcolo, Partial<ScartiFlexCalcolo> | undefined>({
            negativa: 0,
            positiva: 0,
            positivaIntera: 0,
            overflow: false,
            index: 0,
            versoEntrata: false,
        }, json);
    }

    static getSegnoScarto = function (that: ScartiFlexCalcolo) {
        return that.negativa > 0 ? -1 : (that.positiva > 0 ? +1 : 0);
    };

    static getScarto = function (that: ScartiFlexCalcolo) {
        return that.negativa + that.positiva;
    };


    static getScarti = function (that: ScartiFlexCalcolo) {
        return that.negativa + '/' + that.positiva;
    };

    static scarica = function (that: ScartiFlexCalcolo, effettive: CoppiaTimbrature[], compensaDi: number) {
        that.positiva -= compensaDi;
        if (that.positiva < 0) {
            throw 'Quantità di compensazione maggiore del valore consentito! (' + that.index + ')';
        }
        let timbratura;
        timbratura = that.versoEntrata ? CoppiaTimbrature.getEntrata(effettive[that.index]) : CoppiaTimbrature.getUscita(effettive[that.index]);
        timbratura = Calc.hhmm2hhcc(timbratura);
        if (that.versoEntrata) {
            timbratura += Calc.hhmm2hhcc(compensaDi);
            timbratura = Calc.hhcc2hhmm(timbratura);
            CoppiaTimbrature.setEntrata(effettive[that.index], timbratura);
        } else {
            timbratura -= Calc.hhmm2hhcc(compensaDi);
            timbratura = Calc.hhcc2hhmm(timbratura);
            CoppiaTimbrature.setUscita(effettive[that.index], timbratura);
        }
    };

    static compare = function ({positiva: a}: ScartiFlexCalcolo, {positiva: b}: ScartiFlexCalcolo) {
        if (a < b) return -1;
        if (a > b) return 1;
        return 0;
    }
}

export class Flex {
    scartiPrima: ScartiFlexCalcolo[] = [];
    scartiDopo: ScartiFlexCalcolo[] = [];
    flexOverflow = false;
    private readonly day: PresenzeGiornataPlusRecord;
    private readonly flex: FlexRecord;
    private readonly calcoloMassivo: boolean;
    private totaleFlexNegativo = 0;
    private totaleFlexPositivo = 0;

    constructor(day: PresenzeGiornataPlusRecord, tabFlex: FlexRecord, calcoloMassivo: boolean = false) {
        this.day = day;
        this.flex = tabFlex;
        this.calcoloMassivo = calcoloMassivo;
    }

    public applica = (): CoppiaTimbrature[] => {
        if (!this.calcoloMassivo) {
            console.log('Applico regole di flessibilità della tabella ' + this.flex.descrizione + ' [#' + this.flex.idFlex + ']');
        }
        const effettiveFlex: CoppiaTimbrature[] = [];

        this.scartiPrima = []; // [] di ScartiFlex
        this.scartiDopo = []; // [] di ScartiFlex
        let indexTimb = 0;

        // PRIMO: COPIO EFFETTIVE
        let previsteFlex = [];
        let flexScarti = FlexRecord.getScarti(this.flex);
        if (this.flex.timbratureCompensabili === 'PRIMA_ULTIMA') {
            let timbFlex;
            if (this.day.previste.length >= 1) {
                timbFlex = {
                    entrata: this.day.previste[0].entrata,
                    uscita: this.day.previste[this.day.previste.length - 1].uscita
                };
                previsteFlex.push(CoppiaTimbrature.factory(timbFlex));
            }
            if (this.day.effettive.length >= 1) {
                timbFlex = {
                    entrata: this.day.effettive[0].entrata,
                    uscita: this.day.effettive[this.day.effettive.length - 1].uscita
                };
                let copia = CoppiaTimbrature.factory(timbFlex);

                if (CoppiaTimbrature.getEntrata(copia)! <= 1100) // todo prelevare limite intervento da tabella...
                {
                    effettiveFlex.push(copia);
                } else {
                    for (let indEF = 0; indEF < this.day.effettive.length; ++indEF) {
                        effettiveFlex.push(CoppiaTimbrature.factory(this.day.effettive[indEF]));
                    }
                    return effettiveFlex;
                }
            }

        } else {
            for (let indPF = 0; indPF < this.day.previste.length; ++indPF) {
                previsteFlex.push(CoppiaTimbrature.factory(this.day.previste[indPF]));
            }

            for (let indEF = 0; indEF < this.day.effettive.length; ++indEF) {
                effettiveFlex.push(CoppiaTimbrature.factory(this.day.effettive[indEF]));
            }
        }

        // SECONDO: RILEVO SCARTI
        for (let indE = 0; indE < previsteFlex.length; ++indE) {
            let timbraturaPrev = previsteFlex[indE];
            if (indE < effettiveFlex.length) {

                let timbraturaEff = effettiveFlex[indE];

                {
                    let entrataPrev = CoppiaTimbrature.getEntrata(timbraturaPrev);
                    let entrataEff = CoppiaTimbrature.getEntrata(timbraturaEff);
                    if (entrataPrev !== null && entrataEff !== null) {
                        const scarto = ScartiFlexCalcolo.factory({index: indE, versoEntrata: true});
                        if (entrataPrev > entrataEff) { // arrivo prima
                            if (flexScarti[indexTimb].positiva > -1) {
                                const copia = new Coppia(entrataEff, entrataPrev);
                                scarto.positiva = copia.calc.getCount();
                                scarto.positivaIntera = scarto.positiva;
                                scarto.overflow = false;
                                scarto.positiva = Math.min(scarto.positiva, flexScarti[indexTimb].positiva);
                            }
                        } else { // arrivo dopo
                            if (flexScarti[indexTimb].negativa > -1) {
                                const copia = new Coppia(entrataPrev, entrataEff);
                                scarto.negativa = copia.calc.getCount();
                                scarto.overflow = (scarto.negativa > flexScarti[indexTimb].negativa);
                                scarto.negativa = Math.min(scarto.negativa, flexScarti[indexTimb].negativa);
                            }
                        }
                        this.scartiPrima.push(scarto);
                    }
                }

                indexTimb++;

                {
                    let uscitaPrev = CoppiaTimbrature.getUscita(timbraturaPrev);
                    let uscitaEff = CoppiaTimbrature.getUscita(timbraturaEff);
                    if (uscitaPrev !== null && uscitaEff !== null) {
                        const scarto = ScartiFlexCalcolo.factory({index: indE, versoEntrata: false});
                        if (uscitaPrev > uscitaEff) { // esco prima
                            if (flexScarti[indexTimb].negativa > -1) {
                                const copia = new Coppia(uscitaEff, uscitaPrev);
                                scarto.negativa = copia.calc.getCount();
                                scarto.overflow = (scarto.negativa > flexScarti[indexTimb].negativa);
                                scarto.negativa = Math.min(scarto.negativa, flexScarti[indexTimb].negativa);
                            }
                        } else { // esco dopo
                            if (flexScarti[indexTimb].positiva > -1) {
                                const copia = new Coppia(uscitaPrev, uscitaEff);
                                scarto.positiva = copia.calc.getCount();
                                scarto.positivaIntera = scarto.positiva;
                                scarto.overflow = false;
                                scarto.positiva = Math.min(scarto.positiva, flexScarti[indexTimb].positiva);
                            }
                        }
                        this.scartiPrima.push(scarto);
                    }
                }
                indexTimb++;
            } else {
                break;
            }
        }


        // SCARTI RILEVATI!!!
        //console.log(JSON.stringify(this.scartiPrima));

        // TERZO: SOMMO SCARTI POSITIVI E NEGATIVI
        this.totaleFlexNegativo = 0;
        this.totaleFlexPositivo = 0;
        this.flexOverflow = false;
        for (let indE = 0; indE < this.scartiPrima.length; ++indE) {
            // tipoArrotondamento -> enum: MINUTARIO, AL_QUARTO, ALLA_MEZZA, DA_TABELLA
            if (this.flex.tipoArrotondamento === 'AL_QUARTO') {
                let scarto;
                // arrotondo al quarto d'ora
                scarto = Math.ceil(this.scartiPrima[indE].negativa / 15);
                scarto *= 15;
                this.scartiPrima[indE].negativa = scarto;
                scarto = Math.floor(this.scartiPrima[indE].positiva / 15);
                scarto *= 15;
                this.scartiPrima[indE].positiva = scarto;
            } else if (this.flex.tipoArrotondamento === 'ALLA_MEZZA') {
                let scarto;
                // arrotondo al quarto d'ora
                scarto = Math.ceil(this.scartiPrima[indE].negativa / 30);
                scarto *= 30;
                this.scartiPrima[indE].negativa = scarto;
                scarto = Math.floor(this.scartiPrima[indE].positiva / 30);
                scarto *= 30;
                this.scartiPrima[indE].positiva = scarto;
            } else if (this.flex.tipoArrotondamento === 'AL_DECIMO') {
                let scarto;
                // arrotondo al quarto d'ora
                scarto = Math.ceil(this.scartiPrima[indE].negativa / 10);
                scarto *= 10;
                this.scartiPrima[indE].negativa = scarto;
                scarto = Math.floor(this.scartiPrima[indE].positiva / 10);
                scarto *= 10;
                this.scartiPrima[indE].positiva = scarto;
            } else if (this.flex.tipoArrotondamento === 'AL_QUINTO') {
                let scarto;
                // arrotondo al quarto d'ora
                scarto = Math.ceil(this.scartiPrima[indE].negativa / 5);
                scarto *= 5;
                this.scartiPrima[indE].negativa = scarto;
                scarto = Math.floor(this.scartiPrima[indE].positiva / 5);
                scarto *= 5;
                this.scartiPrima[indE].positiva = scarto;
            } /*else if (this.flex.tipoArrotondamento === 'DA_TABELLA') {
                // todo...
            }*/
            /*
            * altrimenti minutario...
            * */
            this.totaleFlexNegativo += this.scartiPrima[indE].negativa;
            this.totaleFlexPositivo += this.scartiPrima[indE].positiva;
            this.flexOverflow = this.flexOverflow || this.scartiPrima[indE].overflow;
            this.scartiDopo.push(ScartiFlexCalcolo.factory(this.scartiPrima[indE]));
        }

        // QUARTO: VERIFICO APPLICABILITA

        if (this.totaleFlexPositivo > 0 && this.totaleFlexNegativo > 0 && (!this.flexOverflow || this.flex.regolaFlex === 'ANCHE_FUORI_LIMITE')) {

            let compensabile = [];
            let indE = 0;
            for (let indS = 0; indS < this.scartiDopo.length;) {
                let compensaCopia = !FlexRecord.isTrasversale(this.flex);
                let totaleCopia = ScartiFlexCalcolo.factory();
                totaleCopia.negativa = this.scartiDopo[indS].negativa + this.scartiDopo[indS + 1].negativa;
                totaleCopia.positiva = this.scartiDopo[indS].positiva + this.scartiDopo[indS + 1].positiva;
                compensabile.push(this.totaleFlexPositivo >= totaleCopia.negativa);

                /*  Il flag <compensaCopia> permette di bypassare la compensazione per copia se quella trasversale è più favorevole...
                    Se la positività delle altre coppie, risolte le negatività di ciascuna, è sufficiente a coprire anche la negatività della coppia in esame evito di compensare copia su copia
                    es. 8		12			13,30	17,30
                        7,58	12,03		13,32	18
                        8		12			13,30	18
                    compenso con i 5' di positività della prima coppia per compensare i 2' di ritardo della seconda coppia senza intaccare lo str...
                */
                if (FlexRecord.isTrasversale(this.flex)) {
                    if ((this.totaleFlexPositivo - totaleCopia.positiva) - (this.totaleFlexNegativo - totaleCopia.negativa) >= totaleCopia.negativa) {
                        compensaCopia = false;
                    }
                }

                if (compensaCopia) {
                    if ((totaleCopia.positiva > 0) &&
                        (totaleCopia.negativa > 0) &&
                        (totaleCopia.positiva >= totaleCopia.negativa || this.flex.regolaFlex === 'FINO_A_CAPIENZA' || this.flex.regolaFlex === 'ANCHE_FUORI_LIMITE')) {
                        // ENTRATA
                        let entrata = CoppiaTimbrature.getEntrata(effettiveFlex[indE]);
                        entrata = Calc.hhmm2hhcc(entrata);
                        if (this.scartiDopo[indS].negativa > 0) {
                            const compensa = Math.min(this.scartiDopo[indS].negativa, totaleCopia.positiva);
                            this.totaleFlexPositivo -= compensa;
                            this.scartiDopo[indS].negativa -= compensa;
                            entrata -= Calc.hhmm2hhcc(compensa);
                        }
                        if (this.scartiDopo[indS].positiva > 0) {
                            const compensa = Math.min(this.scartiDopo[indS].positiva, totaleCopia.negativa);
                            this.totaleFlexNegativo -= compensa;
                            this.scartiDopo[indS].positiva -= compensa;
                            entrata += Calc.hhmm2hhcc(compensa);
                        }
                        entrata = Calc.hhcc2hhmm(entrata);
                        CoppiaTimbrature.setEntrata(effettiveFlex[indE], entrata);

                        // USCITA
                        let uscita = CoppiaTimbrature.getUscita(effettiveFlex[indE]);
                        uscita = Calc.hhmm2hhcc(uscita);
                        if (this.scartiDopo[indS + 1].negativa > 0) {
                            const compensa = Math.min(this.scartiDopo[indS + 1].negativa, totaleCopia.positiva);
                            this.totaleFlexPositivo -= compensa;
                            this.scartiDopo[indS + 1].negativa -= compensa;
                            uscita += Calc.hhmm2hhcc(compensa);
                        }
                        if (this.scartiDopo[indS + 1].positiva > 0) {
                            const compensa = Math.min(this.scartiDopo[indS + 1].positiva, totaleCopia.negativa);
                            this.totaleFlexNegativo -= compensa;
                            this.scartiDopo[indS + 1].positiva -= compensa;
                            uscita -= Calc.hhmm2hhcc(compensa);
                        }
                        uscita = Calc.hhcc2hhmm(uscita);
                        CoppiaTimbrature.setUscita(effettiveFlex[indE], uscita);
                    }
                }
                indE += 1;
                indS += 2;
            }

            if (FlexRecord.isTrasversale(this.flex)) {
                // ORDINO SCARTI
                const scartiOrdinati: ScartiFlexCalcolo[] = [];
                for (let indS = 0; indS < this.scartiDopo.length; ++indS) {
                    scartiOrdinati.push(ScartiFlexCalcolo.factory(this.scartiDopo[indS]));
                }
                scartiOrdinati.sort(ScartiFlexCalcolo.compare);

                // SCARTI RILEVATI!!!
                //console.log(JSON.stringify(scartiOrdinati));

                let indUtile = -1;    // Cerco prima posizione utile nell'array degli scarti positivi (scartiXTimbOrd[ind0].m_positiva>0)
                let indS = 0;
                for (; indS < scartiOrdinati.length; ++indS) {
                    if (indUtile === -1 && scartiOrdinati[indS].positiva > 0) {
                        indUtile = indS;
                    }
                }

                indS = 0;
                for (let indE = 0; indE < effettiveFlex.length; ++indE) {
                    //if (compensabile[indE] === true)
                    for (let indCopia = 0; indCopia < 2; ++indCopia) {
                        if (indS < this.scartiDopo.length) {
                            if ((this.scartiDopo[indS].negativa > 0) && (this.totaleFlexPositivo > 0) && (compensabile[indE] === true)) // FLEX negativa COMPENSABILE
                            {
                                let timbratura;
                                timbratura = (indCopia === 0) ? CoppiaTimbrature.getEntrata(effettiveFlex[indE]) : CoppiaTimbrature.getUscita(effettiveFlex[indE]);
                                timbratura = Calc.hhmm2hhcc(timbratura);

                                let scarto = 0;
                                let negativa = this.scartiDopo[indS].negativa;
                                do {
                                    const compensaDi = Math.min(negativa, scartiOrdinati[indUtile].positiva);
                                    if (compensaDi > 0) {
                                        ScartiFlexCalcolo.scarica(scartiOrdinati[indUtile], effettiveFlex, compensaDi);
                                        scarto += compensaDi;
                                    }
                                    negativa = this.scartiDopo[indS].negativa - scarto;
                                    if (negativa > 0) {
                                        ++indUtile;
                                    }
                                } while ((negativa > 0) && (indUtile < scartiOrdinati.length));
                                this.totaleFlexPositivo -= scarto;

                                if (indCopia === 0) { // ENTRATA
                                    timbratura -= Calc.hhmm2hhcc(scarto);
                                    timbratura = Calc.hhcc2hhmm(timbratura);
                                    CoppiaTimbrature.setEntrata(effettiveFlex[indE], timbratura);
                                } else { // USCITA
                                    timbratura += Calc.hhmm2hhcc(scarto);
                                    timbratura = Calc.hhcc2hhmm(timbratura);
                                    CoppiaTimbrature.setUscita(effettiveFlex[indE], timbratura);
                                }
                            }
                        }
                        ++indS;
                    }
                }
            }
        }

        if (this.flex.timbratureCompensabili === 'PRIMA_ULTIMA') {
            const newEffettiveFlex = [];
            if (this.day.effettive.length === 1) {
                const timb = {
                    entrata: effettiveFlex[0].entrata,
                    uscita: effettiveFlex[0].uscita
                };
                newEffettiveFlex.push(CoppiaTimbrature.factory(timb));
            } else {
                for (let indE = 0; indE < this.day.effettive.length; ++indE) {
                    if (indE === 0) {
                        const timb = {
                            entrata: effettiveFlex[0].entrata,
                            uscita: this.day.effettive[indE].uscita
                        };
                        newEffettiveFlex.push(CoppiaTimbrature.factory(timb));
                    } else if (indE === this.day.effettive.length - 1) {
                        const timb = {
                            entrata: this.day.effettive[indE].entrata,
                            uscita: effettiveFlex[0].uscita
                        };
                        newEffettiveFlex.push(CoppiaTimbrature.factory(timb));
                    } else {
                        newEffettiveFlex.push(CoppiaTimbrature.factory(this.day.effettive[indE]));
                    }
                }
            }
            return newEffettiveFlex;
        } else {
            // Se singola copia, valuto se "anticipare" il previsto per permettere la rilevazione dello straordinario
            // N.B. Quasi custom su AGB, funziona solo per flex parì a metà dello straordinario minimo (esempio flex 15 e xd 30)
            // La "positivaIntera" rappresenta la quantità di minuti totali dell'anticipo in ingresso
            if (this.flex.timbratureCompensabili === 'SINGOLA_COPIA') {
                let indC = 0;
                for (let indS = 0; indS < this.scartiDopo.length;) {
                    if (this.scartiDopo[indS].positiva >= flexScarti[indS].positiva &&
                        this.scartiDopo[indS].positivaIntera < (flexScarti[indS].positiva * 2) &&
                        this.scartiDopo[indS + 1].positiva >= flexScarti[indS + 1].positiva &&
                        this.scartiDopo[indS + 1].positivaIntera < (flexScarti[indS + 1].positiva * 2)) {
                        let timbratura;
                        timbratura = CoppiaTimbrature.getEntrata(this.day.previste[indC]);
                        timbratura = Calc.hhmm2hhcc(timbratura);
                        timbratura -= Calc.hhmm2hhcc(flexScarti[indS].positiva);
                        CoppiaTimbrature.setEntrata(this.day.previste[indC], Calc.hhcc2hhmm(timbratura));
                        timbratura = CoppiaTimbrature.getUscita(this.day.previste[indC]);
                        timbratura = Calc.hhmm2hhcc(timbratura);
                        timbratura -= Calc.hhmm2hhcc(flexScarti[indS + 1].positiva);
                        CoppiaTimbrature.setUscita(this.day.previste[indC], Calc.hhcc2hhmm(timbratura));
                    }
                    indS += 2;
                    indC += 1;
                }
            }
            return effettiveFlex;
        }
    };
}
